import { AliOssImage, NonEmpty, VideoThumbnail } from '@aigc/components';
import { useSubmitFn } from '@aigc/hooks';
import { MoreOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, App, Button, Dropdown, Tag, Typography } from 'antd';
import {
  formatDistanceToNow,
  formatDuration,
  intervalToDuration,
} from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import { match, P } from 'ts-pattern';
import fallbackImage from '../../common/fallback.png';
import { VideoCredit } from '../../credit/components/video-credit';
import { MediaPreviewButton } from '../../file/components/media-preview-button';
import { getImageUrls } from '../../file/utils';
import { VideoTranslationLanguageTag } from '../../statistics/components/video-translation-language-tag';
import { VideoTranslationSceneTypeTag } from '../../statistics/components/video-translation-scene-type-tag';
import { videoModelNameMap } from '../../video-model/model-spec';
import { VideoTranslationOptionTags } from '../../video-translation/components/video-translation-option-tags';
import { getOneVideoGenerationTaskStatusQueryOptions } from '../../video/api';
import {
  cancelVideoTranslationMutation,
  retryLongVideoGenerationTaskMutaion,
} from '../api';
import { TaskStatusCode, TaskType, type VideoTaskDto } from '../model';
import { ScriptSourceTag } from './script-source-tag';
import { TaskStatus } from './task-status';

export const TaskResultItem = (props: {
  task: VideoTaskDto;
  dataUpdatedAt: number;
  enableVideoEdit?: boolean;
  onEditVideo?: (filename: string) => void;
  onShowDetails?: () => void;
}) => {
  const { task, dataUpdatedAt } = props;
  const [enabed, setEnabled] = useState(
    () => !TaskStatus.isSettled(task.status),
  );

  const retry = useMutation(retryLongVideoGenerationTaskMutaion);
  const { data, refetch } = useQuery({
    enabled: enabed,
    ...getOneVideoGenerationTaskStatusQueryOptions({
      id: task.id,
      model: task.model,
    }),
    initialData: task,
    initialDataUpdatedAt: dataUpdatedAt,
    staleTime: 5000,
    refetchInterval: 5000,
    structuralSharing: false,
  });

  const _data = data as VideoTaskDto | undefined;

  const onRetry = useSubmitFn(async () => {
    if (retry.isPending) {
      return;
    }

    await retry.mutateAsync(task.id);
    refetch();
  });

  const { modal, message } = App.useApp();
  const cancel = useMutation(cancelVideoTranslationMutation);

  const handleCancel = useSubmitFn(async () => {
    if (cancel.isPending) {
      return;
    }

    modal.confirm({
      title: '中止任务',
      content:
        '确定要中止该视频翻译任务吗？中止后任务将标记为失败，且不可恢复。',
      okText: '中止',
      okButtonProps: { variant: 'filled', color: 'danger' },
      cancelText: '取消',
      onOk: async () => {
        try {
          await cancel.mutateAsync(task.id);
          message.success('任务已中止');
          refetch();
        } catch {
          message.error('中止失败');
        }
      },
    });
  });

  useEffect(() => {
    setEnabled(!TaskStatus.isSettled(_data?.status));
  }, [_data]);

  if (!_data) {
    return null;
  }

  return (
    <div className="flex items-center">
      <div className="flex-none">
        {match(_data)
          .with({ type: TaskType.视频翻译 }, (task) => {
            if (task.status !== 'succeeded') {
              return (
                <VideoThumbnail
                  size={64}
                  src={task.generate_data?.video?.url}
                  classNames={{ image: 'rounded' }}
                ></VideoThumbnail>
              );
            }

            return (
              <VideoThumbnail
                size={64}
                src={task.filename}
                classNames={{ image: 'rounded' }}
              ></VideoThumbnail>
            );
          })
          .otherwise(() => (
            <AliOssImage
              classNames={{ root: 'flex-none', image: 'object-cover rounded' }}
              size={64}
              src={getImageUrls(_data.generate_data?.file_list)?.at(0)}
              fallback={fallbackImage}
            ></AliOssImage>
          ))}
      </div>
      <div className="ml-2 min-w-0 flex-auto space-y-1">
        <div className="flex items-center space-x-2">
          <Tag># {task.id}</Tag>
          {match(_data)
            .with(
              { type: P.union(TaskType.视频生成, TaskType.长视频生成) },
              (task) => {
                return (
                  <ScriptSourceTag
                    source={task.generate_data?.source}
                  ></ScriptSourceTag>
                );
              },
            )
            .otherwise(() => null)}
          {match(_data)
            .with({ type: TaskType.视频翻译 }, (translationTask) => {
              return (
                <VideoTranslationSceneTypeTag
                  sceneType={
                    translationTask.generate_data?.precheck
                      ?.scene_classification?.scene_type
                  }
                  needs_translation={
                    translationTask.generate_data?.precheck?.needs_translation
                  }
                />
              );
            })
            .otherwise(() => null)}
          <NonEmpty
            value={task.generate_data?.product_name}
            render={(value) => {
              return <div className="truncate">{value}</div>;
            }}
          ></NonEmpty>
          <div className="flex-auto"></div>
          <div className="flex-none space-x-1">
            {_data.filename && (
              <>
                {props.onEditVideo &&
                  TaskStatus.isSucceeded(_data.status) &&
                  _data.filename && (
                    <Button
                      size="small"
                      variant="filled"
                      onClick={() => {
                        props.onEditVideo?.(_data.filename!);
                      }}
                    >
                      编辑视频
                    </Button>
                  )}
                <MediaPreviewButton
                  size="small"
                  variant="filled"
                  color="primary"
                  href={_data.filename}
                >
                  查看视频
                </MediaPreviewButton>
              </>
            )}
            {(_data.type === TaskType.视频生成 ||
              _data.type === TaskType.长视频生成) &&
              props.onShowDetails && (
                <Dropdown
                  placement="bottomRight"
                  trigger={['click']}
                  menu={{
                    items: [
                      {
                        key: 'view',
                        label: '查看脚本',
                        onClick: props.onShowDetails,
                      },
                    ],
                  }}
                >
                  <Button
                    variant="filled"
                    color="default"
                    size="small"
                    icon={<MoreOutlined />}
                  ></Button>
                </Dropdown>
              )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {_data.generate_data?.generation_type === 'frame' && (
            <Tag>首尾帧生成</Tag>
          )}
          {_data.generate_data?.generation_type === 'reference' && (
            <Tag>参考图生成</Tag>
          )}
          <NonEmpty
            value={_data.dna}
            render={(value) => (
              <Typography.Text copyable={{ text: `[${value}]` }}>
                <Tag>DNA: {value}</Tag>
              </Typography.Text>
            )}
          ></NonEmpty>
          {match(task)
            .with(
              {
                type: P.union(
                  TaskType.视频翻译,
                  TaskType.视频翻译_废弃,
                  TaskType.智能擦除,
                  TaskType.画质修复,
                ),
              },
              () => null,
            )
            .otherwise(() => (
              <Tag>{videoModelNameMap.get(task.model) || task.model}</Tag>
            ))}
          {match(_data)
            .with({ type: TaskType.视频翻译 }, (translationTask) => (
              <>
                <VideoTranslationLanguageTag
                  source={
                    translationTask.generate_data?.precheck?.audio_language
                  }
                  target={translationTask.generate_data?.target_language}
                />
                <VideoTranslationOptionTags
                  options={translationTask.generate_data}
                />
              </>
            ))
            .with({ type: TaskType.视频翻译_废弃 }, (translationTask) => (
              <VideoTranslationLanguageTag
                source={undefined}
                target={
                  translationTask.generate_data?.translate_options
                    .target_language
                }
              />
            ))
            .otherwise(() => null)}
          <VideoCredit task={_data}></VideoCredit>
        </div>
        <div className="flex items-center">
          <div className="space-x-2">
            <TaskStatus task={_data}></TaskStatus>
            {TaskStatus.isSettled(task.status) ? (
              <span className="text-xs">
                耗时：
                {formatDuration(
                  intervalToDuration({
                    start: task.created_at,
                    end: task.updated_at ?? 0,
                  }),
                  { locale: zhCN },
                )}
              </span>
            ) : (
              <span className="text-xs">
                {formatDistanceToNow(task.created_at, {
                  locale: zhCN,
                  includeSeconds: true,
                  addSuffix: true,
                })}
              </span>
            )}
          </div>
          <div className="flex-auto"></div>
          <div>
            <span className="text-xs">
              {new Date(task.created_at).toLocaleString()}
            </span>
          </div>
        </div>
        {_data.type === TaskType.视频翻译 &&
          !TaskStatus.isSettled(_data.status) &&
          Date.now() - task.created_at > 20 * 60 * 1000 && (
            <Alert
              type="warning"
              title="当前服务器繁忙，翻译任务进展缓慢，请耐心等待，也可以中止后重新提交"
              action={
                <Button
                  size="small"
                  variant="outlined"
                  color="danger"
                  loading={cancel.isPending}
                  onClick={handleCancel}
                >
                  中止
                </Button>
              }
            ></Alert>
          )}
        {_data.status === TaskStatusCode.任务失败 && _data.error && (
          <Alert
            classNames={{ root: 'mt-2', title: 'break-words break-all' }}
            title={_data.error}
            type="error"
            action={
              _data.type === TaskType.长视频生成 ? (
                <Button
                  size="small"
                  variant="filled"
                  color="danger"
                  loading={retry.isPending}
                  onClick={onRetry}
                >
                  重试
                </Button>
              ) : null
            }
          ></Alert>
        )}
      </div>
    </div>
  );
};
